// API de GWT para Vue 3
// Migrado desde gwt.js
// Proporciona acceso tipado a las funciones GWT

import type {
  Configuration,
  Department,
  Project,
  SessionInformation,
  WorkPlace,
} from "@wkt/api/gwt/gwtDTO";

/**
 * API cliente para comunicación con GWT
 */
export class GWTApi {
  /**
   * Obtiene la información de sesión del usuario actual
   */
  getSessionInformation(): SessionInformation | null {
    try {
      const data = window.$gwtBridge.getSessionInformation();
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error parsing session information:", error);
      return null;
    }
  }

  /**
   * Obtiene todas las configuraciones de la aplicación
   */
  getConfigurations(): Configuration[] {
    try {
      const data = window.$gwtBridge.getConfigurations();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing configurations:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los centros de trabajo
   */
  getWorkplaces(): WorkPlace[] {
    try {
      const data = window.$gwtBridge.getWorkplaces();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing workplaces:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los departamentos
   */
  getDepartments(): Department[] {
    try {
      const data = window.$gwtBridge.getDepartments();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing departments:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los proyectos
   */
  getProjects(): Project[] {
    try {
      const data = window.$gwtBridge.getProjects();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing projects:", error);
      return [];
    }
  }

  /**
   * Descarga un archivo desde el servidor
   */
  downloadFile(url: string, fileName: string): void {
    window.$gwtBridge.downloadFile(url, fileName);
  }

  /**
   * Verifica si la aplicación se está ejecutando en un dispositivo móvil
   */
  isApp(): boolean {
    return window.$gwtBridge.isApp();
  }

  /**
   * Valida CIF/NIF según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validateCIF(countryId: string, value: string): boolean {
    return window.$gwtBridge.validateCIF(countryId, value);
  }

  /**
   * Valida teléfono según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validatePhone(countryId: string, value: string): boolean {
    return window.$gwtBridge.validatePhone(countryId, value);
  }

  /**
   * Valida código postal según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validatePostalCode(countryId: string, value: string): boolean {
    return window.$gwtBridge.validatePostalCode(countryId, value);
  }

  /**
   * Valida separador decimal según locale
   * @param value - Valor a validar
   * @param locale - Locale (no usado en client, mantener '' por compatibilidad)
   */
  validateDecimalSeparator(value: string, locale: string): boolean {
    return window.$gwtBridge.validateDecimalSeparator(value, locale);
  }

  /**
   * Procesa eventos de widgets (para DialogManager)
   * @param mode - Modo del evento
   * @param type - Tipo de evento
   * @param action - Acción a realizar
   * @param payload - Datos del evento
   */
  processEvent(
    mode: string,
    type: string,
    action: string,
    payload: unknown
  ): void {
    if (typeof window.$gwtBridge.processEvent === "function") {
      window.$gwtBridge.processEvent(mode, type, action, payload);
    }
  }
}

// Exportar instancia única (singleton)
export const gwtApi = new GWTApi();

// Exportar como default para compatibilidad
export default gwtApi;
